import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { serialize } from "object-to-formdata";
import { useNavigate  } from "react-router-dom";

import PopupTitle from "components/common/PopupTitle";
import Button, { BUTTON_VARIANT } from "components/common/Button";
import TutorialHint from "components/common/TutorialHint";
import UserForm from "./UserForm";
import CompanyForm from "./CompanyForm";
import CodeForm from "./CodeForm";
import { signUp } from "store/auth";
import Error from "components/Forms/Error";
import { sendAim } from "helpers/statisticAim";
import { trackGoogleConversion } from "utils/analytics";

import { userValidation, companyValidation, codeValidation } from "./validate";

import styles from "./styles.module.scss";

const INITIAL_VALUES = {
    firstname: "",
    lastname: "",
    mobile_phone: "",
    email: "",
    password: "",
    rePassword: "",

    organization_name: "",
    payer_number: null,
    legal_address: "",
    post_address: "",
    company_roles_ids: [],
    logo: null,

    code: "",
};

const RegistrationForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const authenticated = useSelector((state) => state.auth.authenticated);

    const [formStep, setFormStep] = useState(1);
    const [roles, setRoles] = useState([]);
    const [isCodeCreated, setIsCodeCreated] = useState(false);

    const handleRequestRegistration = async (values, setErrors) => {
        const formValues = {};
        for (const field in values) {
            if (
                values[field] !== "" &&
                values[field] !== null &&
                field !== "logo" &&
                field !== "rePassword"
            ) {
                formValues[field] = values[field];
            }
        }

        const formData = serialize(formValues);
        if (values["logo"]) {
            formData.append("logo", values["logo"].file);
        }

        const result = await dispatch(signUp(formData));

        if (result.payload.errors && result.payload.errors.length > 0) {
            const errors = {};
            result.payload.errors.forEach((error) => {
                errors[error.path[0]] = t(error.type);
            });
            setErrors(errors);
        } else {
            sendAim("Registr");
            await trackGoogleConversion();
        }
    };

    const handleSubmit = (values, { setErrors }) => {
        handleRequestRegistration(values, setErrors);
    };

    const handleFormStepBack = async (validateForm) => {
        setFormStep(formStep - 1);
    };

    useEffect(() => {
        if (authenticated) {
            navigate("/profile/company");
        }
    }, [authenticated, navigate]);

    const validation = useMemo(() => {
        if (formStep === 1) {
            return userValidation(t);
        }
        if (formStep === 2) {
            return companyValidation(t);
        }
        if (formStep === 3) {
            return codeValidation(t);
        }
    }, [formStep, t]);

    return (
        <div className={styles.form}>
            <PopupTitle title={t("registration.heading")} withLogo />
            {/* Подсказка видна на всех трёх шагах: ролик разбирает шаг за шагом */}
            <TutorialHint
                tutorialId="registration"
                text={t("tutorials.hintRegistration")}
                className={styles.tutorialHint}
            />
            <Formik
                initialValues={INITIAL_VALUES}
                validationSchema={validation}
                onSubmit={handleSubmit}
            >
                {({ errors, submitCount, setFieldTouched, validateForm, setTouched }) => (
                    <Form>
                        {formStep === 1 && (
                            <UserForm
                                setFieldTouched={setFieldTouched}
                                validateForm={validateForm}
                                setTouched={setTouched}
                                setFormStep={setFormStep}
                            />
                        )}
                        {formStep === 2 && (
                            <CompanyForm
                                setRoles={setRoles}
                                roles={roles}
                                errors={errors}
                                setFieldTouched={setFieldTouched}
                                validateForm={validateForm}
                                setTouched={setTouched}
                                setFormStep={setFormStep}
                            />
                        )}
                        {formStep === 3 && (
                            <CodeForm
                                isCodeCreated={isCodeCreated}
                                setIsCodeCreated={setIsCodeCreated}
                            />
                        )}
                        {submitCount > 0 && formStep === 3 && Object.entries(errors).length > 0 && (
                            <div className={styles.errors}>
                                {Object.keys(errors).map((key, index) => (
                                    <Error key={index}>{`${t(`registration.${key}`)}: ${t(
                                        errors[key],
                                    )}`}</Error>
                                ))}
                            </div>
                        )}
                        <div className={styles.button}>
                            {formStep === 3 && isCodeCreated && (
                                <Button type="submit">{t("registration.sign_in_button")}</Button>
                            )}
                            {formStep !== 1 && (
                                <Button
                                    type="button"
                                    variant={BUTTON_VARIANT.secondary}
                                    onClick={handleFormStepBack}
                                    className={styles.backButton}
                                >
                                    {t("registration.back")}
                                </Button>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default RegistrationForm;
